import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useForm, useFieldArray } from 'react-hook-form';
import ModalCloseButton from './ModalCloseButton';
import guLocale from "../locales/gu";

const HeadModal = ({ showModal, handleClose, handleSubmit, initialData }) => {
    const locale = guLocale.headModal;
    const [numHeads, setNumHeads] = useState('');
    const [isDisabledAddHead, setDisabledAddHead] = useState(true);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [isHeadCountSet, setIsHeadCountSet] = useState(false);
    const [showHeadForm, setShowHeadForm] = useState(false); // New state to control head form visibility

    const { register, handleSubmit: hookFormHandleSubmit, control, formState: { errors }, reset, setValue, getValues } = useForm({
        defaultValues: {
            heads: [],
            num_heads: ''
        }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "heads"
    });

    useEffect(() => {
        if (showModal) {
            if (initialData) {
                reset({
                    heads: [initialData],
                    num_heads: 1
                });
                setNumHeads(1);
                setIsSubmitDisabled(false);
                setIsHeadCountSet(true);
                setShowHeadForm(true); // Show head form when editing
            } else {
                reset({
                    heads: [],
                    num_heads: ''
                });
                setNumHeads('');
                setIsSubmitDisabled(true);
                setIsHeadCountSet(false);
                setShowHeadForm(false); // Initially hide head form for new heads
            }
        }
    }, [showModal, initialData, reset]);

    useEffect(() => {
        setIsSubmitDisabled(!(showHeadForm && numHeads > 0) && !initialData); // Submit disabled if form not shown or no heads and not editing
        if(initialData) {
            setIsSubmitDisabled(false);
        }
    }, [numHeads, initialData, showHeadForm]);


    const handleNumHeadsChange = (e) => {
        let value = e.target.value;
        setDisabledAddHead(false);
        if (isNaN(parseInt(value, 10)) || parseInt(value, 10) <= 0) { // Changed to <= 0 to disable for 0 as well
            value = '';
            setDisabledAddHead(true);
            setNumHeads('');
        } else {
            setNumHeads(value);
        }
        setValue("num_heads", value, { shouldValidate: false });
    };


    const handleAddHeads = () => {
        let currentNumHeads = parseInt(getValues("num_heads"), 10);
        if (isNaN(currentNumHeads) || currentNumHeads <= 0) { // Changed to <= 0 to handle 0 and invalid input
            currentNumHeads = 0;
            setNumHeads(''); // Clear numHeads if invalid or 0
            setValue("num_heads", '', { shouldValidate: false });
            setShowHeadForm(false); // Hide head form if invalid or 0 heads requested
            setIsHeadCountSet(false); // Reset head count set state
            setIsSubmitDisabled(true); // Disable submit button
            return; // Exit early if input is invalid or 0
        }


        if (currentNumHeads > 0) {
            setNumHeads(currentNumHeads);
            if (currentNumHeads > fields.length) {
                append(Array.from({ length: currentNumHeads - fields.length }, () => ({ head_name: "", opening_balance_cash: "", opening_balance_bank: "" })));
            } else if (currentNumHeads < fields.length) {
                while (fields.length > currentNumHeads) {
                    remove(fields.length - 1);
                }
            }
            setShowHeadForm(true); // Show head form after "Add Head" click with valid count
            setIsHeadCountSet(true);
            setIsSubmitDisabled(false); // Enable submit button after adding heads
        } else {
            setShowHeadForm(false); // Hide head form if 0 heads (though this case should be handled above)
            setIsHeadCountSet(false);
            setIsSubmitDisabled(true);
        }

    };

    const handleFormSubmit = async (data) => {
        try {
            await handleSubmit(data.heads);
            handleClose();
        } catch (error) {
            console.error("Error submitting form data", error);
        }
    };

    const handleModalClose = () => {
        setNumHeads('');
        reset({
            heads: [],
            num_heads: ''
        });
        setIsSubmitDisabled(true);
        setIsHeadCountSet(false);
        setShowHeadForm(false); // Hide head form on modal close
        handleClose();
    };

    const formGroupStyle = {
        flex: 1,
        maxWidth: 'calc(32% - 10px)',
    };

    const fieldSetContainerStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        marginBottom: '15px',
    };

    const deleteButtonStyle = {
        padding: '0.375rem 0.75rem',
        alignSelf: 'center',
        marginBottom: '15px',
    };

    return (
        <Modal show={showModal} onHide={handleModalClose} centered size="xl">
            <Modal.Header>
                <Modal.Title>{initialData ? locale.editHeadModalTitle : locale.addNewHeadsModalTitle}</Modal.Title>
                <ModalCloseButton onClick={handleModalClose} />
            </Modal.Header>

            <Modal.Body>
                <Form onSubmit={hookFormHandleSubmit(handleFormSubmit)}>
                    {!initialData && !isHeadCountSet && (
                        <Card className="mb-3">
                            <Card.Body>
                                <Form.Group className="mb-3" controlId="num_heads_group">
                                    <Form.Label>{locale.howManyHeadsLabel}</Form.Label>
                                    <Row className="align-items-center">
                                        <Col>
                                            <Form.Control
                                                type="text"
                                                min="0"
                                                value={numHeads}
                                                onChange={handleNumHeadsChange}
                                                name="num_heads"
                                                placeholder={locale.enterNumHeadsPlaceholder}
                                            />
                                        </Col>
                                        <Col xs="auto">
                                            <Button variant="primary" disabled={isDisabledAddHead} onClick={handleAddHeads}>
                                                <FontAwesomeIcon icon={faPlus} /> {locale.addButtonText}
                                            </Button>

                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    )}

                    {showHeadForm && numHeads > 0 && ( // Conditionally render head form based on showHeadForm and numHeads
                        <Card>
                            <Card.Body>
                                {fields.map((item, index) => (
                                    <div key={item.id} style={fieldSetContainerStyle}>
                                        <Form.Group className="mb-3" controlId={`heads[${index}].head_name`} style={formGroupStyle}>
                                            {index === 0 && <Form.Label>{locale.headNameLabel}</Form.Label>}
                                            <Form.Control
                                                type="text"
                                                placeholder={locale.enterHeadNamePlaceholder}
                                                {...register(`heads[${index}].head_name`, {
                                                    required: locale.headNameRequired,
                                                })}
                                                isInvalid={!!errors.heads?.[index]?.head_name}
                                                readOnly={item.head_name === 'વ્યાજ' || item.head_name === 'પદર'}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.heads?.[index]?.head_name?.message}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId={`heads[${index}].opening_balance_cash`} style={formGroupStyle}>
                                            {index === 0 && <Form.Label>{locale.cashAmountLabel}</Form.Label>}
                                            <Form.Control
                                                type="number"
                                                placeholder={locale.enterCashAmountPlaceholder}
                                                {...register(`heads[${index}].opening_balance_cash`, {
                                                    required: locale.cashAmountRequired,
                                                    valueAsNumber: true,
                                                })}
                                                isInvalid={!!errors.heads?.[index]?.opening_balance_cash}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.heads?.[index]?.opening_balance_cash?.message}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId={`heads[${index}].opening_balance_bank`} style={formGroupStyle}>
                                            {index === 0 && <Form.Label>{locale.bankAmountLabel}</Form.Label>}
                                            <Form.Control
                                                type="number"
                                                placeholder={locale.enterBankAmountPlaceholder}
                                                {...register(`heads[${index}].opening_balance_bank`, {
                                                    required: locale.bankAmountRequired,
                                                    valueAsNumber: true,
                                                })}
                                                isInvalid={!!errors.heads?.[index]?.opening_balance_bank}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.heads?.[index]?.opening_balance_bank?.message}</Form.Control.Feedback>
                                        </Form.Group>

                                        {index > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                                <Button variant="danger" onClick={() => remove(index)} style={deleteButtonStyle}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalClose}>
                    {locale.cancelButtonText}
                </Button>
                <Button variant="primary" type="submit" onClick={hookFormHandleSubmit(handleFormSubmit)} disabled={isSubmitDisabled}>
                    {locale.submitButtonText}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default HeadModal;